import '../config/teams.json'
import { useSocketEvent, useSocketState } from '../helpers/useSocket';
import { Bar1v1 } from '../components/Bar100';
import colors from '../config/colors.json'
import { TeamIcon, StripTeamTag } from '../components/TeamIcon';
import ChartjsPluginAnnotation from "chartjs-plugin-annotation";
import React, { useState, useEffect } from 'react';
import { socket } from '../socket';

import { convertSummary } from '../helpers/convertSummary'
import { fancyTimeFormat } from '../helpers/fancyTimeFormat'

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import { Stats } from '../components/Stats';
import { MilitaryChart } from '../components/MilitaryChart';
import { EconomyChart } from '../components/EconomyChart';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartjsPluginAnnotation,
);

export default function Display() {

  const [isConnected, setIsConnected] = useState(socket.connected);
  const [data, setData] = useState()
  const [militaryLead, setMilitaryLead] = useState()
  const [summary, setSummary] = useState({})
  const [config, setConfig] = useState({});

  const [annotations, setAnnotations] = useState([])

  const [isReady, setIsReady] = useState(false)
  const [player1win, setPlayer1win] = useState(true)

  const [extraInfo, setExtraInfo] = useState({})
  const [match] = useSocketState('Match')
  const [tournament] = useSocketState('Tournament')

  useSocketEvent('updateCurrent', (newdata) => {
    setData(newdata.summary)
    setExtraInfo(newdata.extra)
  })

  useEffect(() => {
    socket.emit('registerPostgame');
  }, []);
  useSocketEvent('updateConfig', (newdata) => {
    console.log(`Received updateConfig`, newdata);
    setConfig(newdata)
  })

  useEffect(() => {
    if (!data || !data.players || !match) {
        setIsReady(false)
        return;
    }

    let _summary = convertSummary(data);

    let _annotations = []

    // Swap players if extrainfo wants it
    if (_summary && _summary?.players.length === 2) {
      const p0name = _summary.players[0].name;
      const p1name = _summary.players[1].name;
      const tournamentPlayers = tournament?.config?.players;
      const p0stripped = StripTeamTag(p0name, tournamentPlayers).trim();
      const p1stripped = StripTeamTag(p1name, tournamentPlayers).trim();
      if (p1name === match?.leftPlayer.name || p0name === match?.rightPlayer.name ||
        p1stripped === match?.leftPlayer.name || p0stripped === match?.rightPlayer.name ||
        match?.leftPlayer.altNames?.includes(p1name) || match?.rightPlayer.altNames?.includes(p0name) ||
        match?.leftPlayer.altNames?.includes(p1stripped) || match?.rightPlayer.altNames?.includes(p0stripped)
      ) {
        _summary.players = [_summary.players[1], _summary.players[0]]
      }
    }

    _summary?.players.forEach((player, index) => {
      const matchPlayer = index ? match.rightPlayer : match.leftPlayer;
      const playerColor = colors[matchPlayer.color];

      if (player.feudalAge) {
        _annotations.push({
          type: 'line',
          scaleID: 'x',
          value: player.feudalAge,
          borderColor: playerColor?.mainColor,
          borderWidth: 4,
          borderDash: [4, 4],
          label: {
            display: true,
            backgroundColor: 'rgba(0,0,0,0.1)',
            color: playerColor?.mainColor,
            position: index ? '5%' : '15%',
            font: { size: 20 },
            padding: { left: 4, top: 4, right: 4, bottom: 2 },
            content: age2img
          }
        });
      }
      if (player.castleAge) {
        _annotations.push({
          type: 'line',
          scaleID: 'x',
          value: player.castleAge,
          borderColor: playerColor?.mainColor,
          borderWidth: 4,
          borderDash: [4, 4],
          label: {
            display: true,
            backgroundColor: 'rgba(0,0,0,0.1)',
            color: playerColor?.mainColor,
            position: index ? '5%' : '15%',
            font: { size: 20 },
            padding: { left: 4, top: 4, right: 4, bottom: 2 },
            content: age3img,
          }
        });
      }
      if (player.imperialAge) {
        _annotations.push({
          type: 'line',
          scaleID: 'x',
          value: player.imperialAge,
          borderColor: playerColor?.mainColor,
          borderWidth: 4,
          borderDash: [4, 4],
          label: {
            display: true,
            backgroundColor: 'rgba(0,0,0,0.1)',
            color: playerColor?.mainColor,
            position: index ? '5%' : '15%',
            font: { size: 20 },
            padding: { left: 4, top: 4, right: 4, bottom: 2 },
            content: age4img
          }
        });
      }
    });

    setMilitaryLead(_summary?.players[0].timeline.map((v, i) => ({ x: v.time, y: v.score_military - _summary?.players[1].timeline[i].score_military })))

    if (_summary.players[1].result === "win") {
      setPlayer1win(false)
    } else {
      setPlayer1win(true)
    }

    setSummary(_summary)
    setAnnotations(_annotations)

    setIsReady(true)
  }, [data, !!match, match?.leftPlayer.name, tournament?.config?.players])

  function onUpdateConfig(value) {
    console.log(`Received updateConfig`, value);
    setConfig((oldState) => ({ ...oldState, ...value }));
  }

  const age2img = new Image(30, 30)
  age2img.src = "/age_icons/age_2_dark.png"
  const age3img = new Image(30, 30)
  age3img.src = "/age_icons/age_3_dark.png"
  const age4img = new Image(30, 30)
  age4img.src = "/age_icons/age_4_dark.png"

  document.body.style.overflow = "hidden";

  return (
    <div className="postgame" style={{
      "--left-player-color": colors[match?.leftPlayer?.color]?.mainColor,
      "--left-player-color-secondary": colors[match?.leftPlayer?.color]?.secondaryColor,
      "--right-player-color": colors[match?.rightPlayer?.color]?.mainColor,
      "--right-player-color-secondary": colors[match?.rightPlayer?.color]?.secondaryColor
    }}>
      <link rel="stylesheet" type="text/css" href={`/themes/${config.cssTheme}`} />
      {isReady && !!data && !!match ?
        <div className='flags'>
          <div className='flags__left'>
            <img className={`flags__left__img`} src={`/civFlags/${summary?.players[0].civilization}.png`} alt="Civ" />
          </div>
          <div className='flags__right'>
            <img className={`flags__right__img`} src={`/civFlags/${summary?.players[1].civilization}.png`} alt="Civ" />
          </div>


          <div className="header">
            <div className='header__leftPlayer header__player'>
              <div className='header__leftPlayer__name_civ'>
                <div className={`header__leftPlayer__name header__playerName ${player1win ? 'header__winner' : 'header__loser'}`}>{StripTeamTag(summary?.players[0].name, tournament?.config?.players).replace("Beell","BEE")}</div>
                <div className={`header__leftPlayer__civ header__playerCiv ${player1win ? 'header__winner' : 'header__loser'}`}>{summary?.players[0].civilization.replace(/_/g, ' ').toUpperCase()}</div>
              </div>
              <TeamIcon playerName={summary?.players[0].name} players={tournament?.config?.players} className={`header__leftPlayer__team ${player1win ? 'header__winner' : 'header__loser'}`} />
            </div>

            <div className={`header__leftResult header__result ${player1win ? 'header__winner' : 'header__loser'}`}>{summary?.players[0].result.toUpperCase()}</div>

            <div className={`header__leftScore header__score ${player1win ? 'header__winner' : 'header__loser'}`}>{match.leftPlayer.score}</div>

            <div className="header__duration_mapName">
              <div className="header__duration">{fancyTimeFormat(summary?.duration)}</div>
              <div className="header__mapName">{summary?.mapName.toUpperCase().replace('EGC - ', '').replace('EGCMS - ', '').replace('KOTR - ', '')}</div>
            </div>

            <div className={`header__rightScore header__score ${player1win ? 'header__loser' : 'header__winner'}`}>{match.rightPlayer.score}</div>

            <div className={`header__rightResult header__result ${player1win ? 'header__loser' : 'header__winner'}`}>{summary?.players[1].result.toUpperCase()}</div>

            <div className='header__rightPlayer header__player'>
              <TeamIcon playerName={summary?.players[1].name} players={tournament?.config?.players} className={`${player1win ? 'header__loser' : 'header__winner'} header__rightPlayer__team`} />
              <div className='header__rightPlayer__name_civ '>
                <div className={`header__rightPlayer__name header__playerName ${player1win ? 'header__loser' : 'header__winner'}`}>{StripTeamTag(summary?.players[1].name, tournament?.config?.players).replace("Beell","BEE")}</div>
                <div className={`header__rightPlayer__civ header__playerCiv ${player1win ? 'header__loser' : 'header__winner'}`}>{summary?.players[1].civilization.replace(/_/g, ' ').toUpperCase()}</div>
              </div>
            </div>

          </div>

          <hr className="divider" />

          <div className='resources'>
            <div className='resources__container' >
              <div className='resources__container__bars'>
                <Bar1v1 name="TOTAL RESOURCES GATHERED" player1={summary?.players[0].totalResourcesGathered.total} player2={summary?.players[1].totalResourcesGathered.total} leftColor={colors[match.leftPlayer.color]?.mainColor} rightColor={colors[match.rightPlayer.color]?.mainColor} />
                <Bar1v1 name="FOOD" player1={summary?.players[0].totalResourcesGathered.food} player2={summary?.players[1].totalResourcesGathered.food} leftColor={colors[match.leftPlayer.color]?.mainColor} rightColor={colors[match.rightPlayer.color]?.mainColor} />
                <Bar1v1 name="WOOD" player1={summary?.players[0].totalResourcesGathered.wood} player2={summary?.players[1].totalResourcesGathered.wood} leftColor={colors[match.leftPlayer.color]?.mainColor} rightColor={colors[match.rightPlayer.color]?.mainColor} />
                <Bar1v1 name="GOLD" player1={summary?.players[0].totalResourcesGathered.gold} player2={summary?.players[1].totalResourcesGathered.gold} leftColor={colors[match.leftPlayer.color]?.mainColor} rightColor={colors[match.rightPlayer.color]?.mainColor} />
                {
                  (summary?.players[0].totalResourcesGathered.stone !== 0 || summary?.players[1].totalResourcesGathered.stone !== 0) &&
                  <Bar1v1 name="STONE" player1={summary?.players[0].totalResourcesGathered.stone} player2={summary?.players[1].totalResourcesGathered.stone} leftColor={colors[match.leftPlayer.color]?.mainColor} rightColor={colors[match.rightPlayer.color]?.mainColor} />
                }
                {
                  (summary?.players[0].totalResourcesGathered.oliveoil !== 0 || summary?.players[1].totalResourcesGathered.oliveoil !== 0) &&
                  <Bar1v1 name="OLIVE OIL" player1={summary?.players[0].totalResourcesGathered.oliveoil} player2={summary?.players[1].totalResourcesGathered.oliveoil} leftColor={colors[match.leftPlayer.color]?.mainColor} rightColor={colors[match.rightPlayer.color]?.mainColor} />
                }
              </div>

              <div className="stats">
                <Stats summary={summary} extraInfo={extraInfo} />
              </div>
            </div>

            <div className="charts">
              <MilitaryChart match={match} militaryLead={militaryLead} annotations={annotations} height="355px" />
              <EconomyChart match={match} summary={summary} annotations={annotations} height="355px" />
            </div>
          </div>
          <div className='dataProvider'>Data provided by aoe4world.com</div>
        </div>

        : <></>}

    </div>
  );

}
